/**
 * The storm's colors, one meaning each (docs/agents/plans/lightning-machine.md, "What each effect means"). The night is
 * a deep blue-black; the towers are dark glass with steel edges; blue is the floor's grid, structure and not data.
 * Violet is a pick, always lightning; amber is math happening; mint is numbers moving; the working memory is a
 * white-hot orb whose numbers each show in a color of their own (`valueColor`: blues to ice below zero, greens to pale
 * yellow above); the records are cool and pale, like something kept; rose is the stop token and nothing else.
 *
 * Hex values are sRGB as designed; `C` holds them in the linear light the shaders and the drawing work in, `CSS` as
 * they go on the page.
 */
import type { RGB } from "~/components/DeepDive/engine/math";

const HEX = {
  /** The night at the horizon. */
  night: "#04060C",
  /** The cloud deck, unlit. */
  cloud: "#151B2A",
  /** The towers' glass. */
  glass: "#0A0F18",
  /** The towers' edges and the line: cool steel. */
  steel: "#8FA3C4",
  /** The floor grid: dim, pulsing; structure, not data. */
  blue: "#3E6FB8",
  /** A pick: the lightning, the struck records, the chosen expert, the chosen token. */
  violet: "#A88BFF",
  /** The lightning's white core. */
  bolt: "#F1EDFF",
  /** Math happening: a working ring, a unit, the scores warming the thunderhead. */
  amber: "#FFB04A",
  /** Numbers moving: packets on the links. */
  mint: "#5FF0D0",
  /** The working memory's orb. */
  orb: "#E8F6FF",
  /** Its halo, ball lightning's blue. */
  halo: "#6FD3FF",
  /** A working memory number: zero, then from small to large below zero and above (`valueColor`). */
  zero: "#1A2234",
  neg1: "#1E3486",
  neg2: "#2F5BF5",
  neg3: "#4C9BFF",
  neg4: "#A4EAFF",
  pos1: "#124F3A",
  pos2: "#27B85E",
  pos3: "#A6E83C",
  pos4: "#F4F88E",
  /** The records: kept, cool, pale. */
  record: "#3F86B0",
  /** The indexer's keys, the records' tips. */
  tip: "#B4C2FF",
  /** The stop token, and nothing else. */
  rose: "#FF4E6E"
} as const;

const linear = (x: number) => (x <= 0.04045 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4));
const rgb = (hex: string): RGB => [1, 3, 5].map((i) => linear(parseInt(hex.slice(i, i + 2), 16) / 255)) as RGB;

export type Swatch = keyof typeof HEX;

export const C = Object.fromEntries(Object.entries(HEX).map(([k, v]) => [k, rgb(v)])) as Record<Swatch, RGB>;
export const CSS = HEX;

/** A color brighter than itself, for the bloom to pick up: `k` times, past white where it glows. */
export const glow = (c: RGB, k: number): RGB => [c[0] * k, c[1] * k, c[2] * k];

/**
 * The ramps a working memory number's color runs along, from zero out to 1 below zero and above: it changes hue as
 * well as brightness, so a number that changes is seen to change, not only to dim or brighten (blues to ice below
 * zero; greens through chartreuse to pale yellow above, a family no meaning in the show uses).
 */
export const VALUE_RAMPS = {
  neg: [C.zero, C.neg1, C.neg2, C.neg3, C.neg4],
  pos: [C.zero, C.pos1, C.pos2, C.pos3, C.pos4]
};

/** A working memory number's color, by its sign and size (`VALUE_RAMPS`). */
export function valueColor(v: number): RGB {
  const ramp = v < 0 ? VALUE_RAMPS.neg : VALUE_RAMPS.pos;
  const x = Math.min(1, Math.abs(v)) * (ramp.length - 1);
  const i = Math.min(ramp.length - 2, Math.floor(x));
  const k = x - i;
  const a = ramp[i];
  const b = ramp[i + 1];
  return [a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k, a[2] + (b[2] - a[2]) * k];
}
